import React, { useMemo, useState } from 'react';
import { ChevronDown, Monitor, Smartphone, TabletSmartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { useLocale } from '../../contexts/LocaleContext';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { trackCtaClicked } from '../../utils/analytics';
import {
  getSuggestedInstallPlatform,
  type InstallPlatform,
} from '../../utils/install-platform';
import { GooglePlayBadge } from './GooglePlayBadge';

const CARD_BASE_STYLE: React.CSSProperties = {
  border: '1px solid #e7e7e8',
  borderRadius: '20px',
  padding: '32px 28px',
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
};

const ICON_BOX_STYLE: React.CSSProperties = {
  width: '54px',
  height: '54px',
  borderRadius: '14px',
  background: '#effaf2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--scribane-green)',
  marginBottom: '28px',
  flexShrink: 0,
};

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '21px',
  fontWeight: 700,
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
  margin: '0 0 14px',
  color: 'rgb(39,39,41)',
  lineHeight: 1.2,
};

const BODY_STYLE: React.CSSProperties = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '14px',
  lineHeight: '26px',
  fontWeight: 400,
  color: '#686869',
  margin: '0 0 22px',
};

function getHighlightClass(platform: InstallPlatform, card: InstallPlatform): string {
  return platform === card
    ? 'ring-2 ring-[var(--scribane-green)] ring-offset-2'
    : '';
}

interface PlatformCardProps {
  platform: InstallPlatform;
  highlightPlatform: InstallPlatform;
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}

const PlatformCard: React.FC<PlatformCardProps> = ({
  platform,
  highlightPlatform,
  icon,
  title,
  description,
  children,
}) => (
  <article
    className={['install-app-card', getHighlightClass(highlightPlatform, platform)].join(' ')}
    style={CARD_BASE_STYLE}
  >
    <div style={ICON_BOX_STYLE}>{icon}</div>
    <h3 style={TITLE_STYLE}>{title}</h3>
    <p style={BODY_STYLE}>{description}</p>
    <div>{children}</div>
  </article>
);

const ComingSoonBadge: React.FC<{ label: string }> = ({ label }) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 14px',
      borderRadius: '10px',
      border: '1px solid #e7e7e8',
      background: '#f5f5f5',
      opacity: 0.75,
    }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.63 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
    <span
      style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '13px',
        fontWeight: 600,
        color: '#686869',
      }}
    >
      {label}
    </span>
  </div>
);

export const InstallApps: React.FC = () => {
  const { t } = useLocale();
  const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const highlightPlatform = useMemo(() => getSuggestedInstallPlatform(), []);

  const handleDesktopInstall = (): void => {
    trackCtaClicked(t('landing_apps_desktop_cta'), 'apps_desktop');
    void promptInstall();
  };

  const handlePlayStoreClick = (): void => {
    trackCtaClicked(t('landing_apps_android_cta'), 'apps_play_store');
  };

  const handleIosHintToggle = (): void => {
    trackCtaClicked(t('landing_apps_apple_cta'), 'apps_ios_hint');
    setIosHintOpen((open) => !open);
  };

  return (
    <section id="apps" className="scroll-mt-24" style={{ background: '#f5f5f5', padding: '110px 0 120px' }}>
      <style>{`
        .install-app-card {
          transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                      box-shadow 220ms cubic-bezier(0.22,1,0.36,1),
                      border-color 220ms;
        }
        .install-app-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(39,39,41,0.10), 0 2px 6px rgba(39,39,41,0.05);
          border-color: rgba(39,39,41,0.18);
        }
      `}</style>
      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
        <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 64px' }}>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: '16px',
              lineHeight: '20px',
              fontWeight: 500,
              letterSpacing: '3.2px',
              textTransform: 'uppercase',
              color: 'var(--scribane-green)',
              margin: '0 0 22px',
            }}
          >
            {t('landing_apps_aux')}
          </p>
          <h2
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(34px, 5vw, 56px)',
              lineHeight: 1.06,
              letterSpacing: '-0.02em',
              margin: 0,
              color: 'rgb(39,39,41)',
            }}
          >
            {t('landing_apps_title')}
          </h2>
          <p
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: '18px',
              lineHeight: '24px',
              fontWeight: 400,
              color: 'rgba(39,39,41,0.7)',
              margin: '22px auto 0',
              maxWidth: '620px',
            }}
          >
            {t('landing_apps_subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-7 max-w-[520px] mx-auto min-[900px]:grid-cols-3 min-[900px]:max-w-none min-[900px]:mx-0">
          <PlatformCard
            platform="desktop"
            highlightPlatform={highlightPlatform}
            icon={<Monitor size={24} strokeWidth={2} />}
            title={t('landing_apps_desktop_title')}
            description={t('landing_apps_desktop_desc')}
          >
            {isInstalled ? (
              <p style={{ ...BODY_STYLE, margin: 0, color: 'var(--scribane-green)', fontWeight: 600 }}>
                {t('landing_apps_desktop_installed')}
              </p>
            ) : canInstall ? (
              <Button
                onClick={handleDesktopInstall}
                className="rounded-[10px] py-[11px] px-5 h-auto text-[14px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] transition-[background,color] duration-[220ms]"
              >
                {t('landing_apps_desktop_cta')}
              </Button>
            ) : (
              <p style={{ ...BODY_STYLE, margin: 0, fontSize: '13px' }}>
                {t('landing_apps_desktop_hint')}
              </p>
            )}
          </PlatformCard>

          <PlatformCard
            platform="android"
            highlightPlatform={highlightPlatform}
            icon={<Smartphone size={24} strokeWidth={2} />}
            title={t('landing_apps_android_title')}
            description={t('landing_apps_android_desc')}
          >
            <div className="flex flex-col items-start gap-3">
              <GooglePlayBadge onClick={handlePlayStoreClick} />
              {canInstall && !isInstalled && (
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClicked(t('landing_apps_android_browser_hint'), 'apps_android_browser');
                    void promptInstall();
                  }}
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: '13px',
                    color: 'var(--scribane-green)',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                  }}
                >
                  {t('landing_apps_android_browser_hint')}
                </button>
              )}
            </div>
          </PlatformCard>

          <PlatformCard
            platform="apple"
            highlightPlatform={highlightPlatform}
            icon={<TabletSmartphone size={24} strokeWidth={2} />}
            title={t('landing_apps_apple_title')}
            description={t('landing_apps_apple_desc')}
          >
            <div className="flex flex-col items-start gap-3">
              {isInstalled ? (
                <p style={{ ...BODY_STYLE, margin: 0, color: 'var(--scribane-green)', fontWeight: 600 }}>
                  {t('landing_apps_apple_installed')}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleIosHintToggle}
                  className="flex items-center gap-2"
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--scribane-black)',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                  aria-expanded={iosHintOpen}
                >
                  {t('landing_apps_apple_cta')}
                  <ChevronDown
                    size={16}
                    className={['transition-transform', iosHintOpen ? 'rotate-180' : ''].join(' ')}
                  />
                </button>
              )}
              <ComingSoonBadge label={t('landing_apps_apple_coming_soon')} />
              {iosHintOpen && !isInstalled && (
                <p
                  style={{
                    ...BODY_STYLE,
                    margin: 0,
                    fontSize: '13px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    background: '#effaf2',
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  {t('pwa_install_banner_ios_hint')}
                </p>
              )}
            </div>
          </PlatformCard>
        </div>

        <p
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(39,39,41,0.55)',
            textAlign: 'center',
            margin: '48px 0 0',
          }}
        >
          {t('landing_apps_trust_line')}
        </p>
      </div>
    </section>
  );
};
